import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import { SelectProps } from "antd/lib/select";
import { FilterButtonGroup } from "../filter-button-group";
import { InfiniteScrollSelect } from "../../infinite-scroll-select";
import filterStyles from "../filter.css";

const {
  ["filter-dropdown"]: filterDropdownClassName,
  ["filter-content"]: filterContentClassName,
  ["select"]: selectClassName,
  ["no-options"]: noOptionsClassName,
} = filterStyles;

export type SelectFilterOptionData = {
  value: number;
  label: string;
  [prop: string]: any;
};

export type SelectFilterProps = {
  optionsLabel: string;
  onApplyFilter: (values: number[]) => void;
  onClearFilter: () => void;
  onLoadMore?: () => Promise<OptionsType | void> | OptionsType | void;
} & Pick<SelectProps<number[]>, "options" | "defaultValue" | "onSearch" | "loading">;

const chooseNoOptionsText = (loading: boolean, searchValue: string, optionsLabel: string) => {
  if (loading) return `Loading ${optionsLabel}...`;
  if (searchValue) return `No ${optionsLabel} found`;
  return `No ${optionsLabel} available`;
};

export const SelectFilter = ({
  optionsLabel,
  defaultValue,
  options,
  loading,
  onSearch,
  onApplyFilter,
  onClearFilter,
  onLoadMore,
}: SelectFilterProps) => {
  const [canApply, setCanApply] = useState(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedOptions, setSelectedOptions] = useState<any[]>(defaultValue || []);

  useEffect(() => {
    setSelectedOptions(defaultValue || []);
  }, [defaultValue]);

  const onSelect = (value: any) => {
    setCanApply(true);
    if (value) {
      setSelectedOptions([...selectedOptions, value]);
    }
    setSearchValue("");
  };

  const onDeselect = (value: any) => {
    setCanApply(true);
    setSelectedOptions(selectedOptions.filter((v) => v !== value));
  };

  const search = (value: string) => {
    setSearchValue(value || "");
    onSearch && onSearch(value || "");
  };

  const onClickApply = () => {
    setCanApply(false);
    onApplyFilter && onApplyFilter(selectedOptions);
  };

  const onClickClear = () => {
    setSelectedOptions([]);
    setSearchValue("");
    setCanApply(false);
    onClearFilter && onClearFilter();
  };

  const NoOptions = () => (
    <div className={noOptionsClassName}>
      <h4>{chooseNoOptionsText(loading || false, searchValue, optionsLabel)}</h4>
      {loading && <Spin />}
    </div>
  );

  return (
    <div className={filterDropdownClassName}>
      <FilterButtonGroup
        canApply={canApply}
        canClear={selectedOptions?.length > 0}
        onClickApply={onClickApply}
        onClickClear={onClickClear}
      />
      <div className={filterContentClassName}>
        <InfiniteScrollSelect
          className={selectClassName}
          bordered={false}
          dropdownMatchSelectWidth
          placeholder="Type here for filter options"
          notFoundContent={<NoOptions />}
          autoFocus
          mode="multiple"
          filterOption={false}
          loading={loading}
          defaultOpen={false}
          options={options}
          value={selectedOptions}
          defaultValue={defaultValue}
          searchValue={searchValue}
          onSearch={search}
          dropdownRender={(menu) => <div>{menu}</div>}
          onSelect={(_, option) => onSelect(option.value)}
          onDeselect={(_, option) => onDeselect(option.value)}
          onLoadMore={onLoadMore}
        />
      </div>
    </div>
  );
};
